import {
  valuationFormSchema,
  PROPERTY_TYPES,
  RELATIONSHIP_OPTIONS,
  BEDROOM_OPTIONS,
  WHEN_OPTIONS,
  REASON_OPTIONS,
  HOUSE_STYLES,
  BUNGALOW_STYLES,
  TENANT_REASON_OPTIONS,
} from '@propertypal/shared/src/constants/instantValuation';
import useLoqate from '@propertypal/shared/src/hooks/useLoqate';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { FormValues, ValuationResult } from '@propertypal/shared/src/types/valuations';
import { getValuationPayload } from '@propertypal/shared/src/utils/valuations';
import Button from '@propertypal/web-ui/src/buttons/Button';
import ErrorMessage from '@propertypal/web-ui/src/errors/ErrorMessage';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import TextInput from '@propertypal/web-ui/src/inputs/TextInput';
import { Row } from '@propertypal/web-ui/src/layout';
import Recaptcha, { getCaptchaKey } from '@propertypal/web-ui/src/security/Recaptcha';
import { useFormik } from 'formik';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import config from '../../../../../app.config.json';

interface Props {
  mode: 'agent' | 'instant';
  onResult: (nextStep: string, result?: ValuationResult) => void;
  onPostcodeUpdate: (postcode: string) => void;
  address?: string;
  loqId?: string;
  pc?: string;
}

const InstantValuationForm: FunctionComponent<Props> = (props) => {
  const [loading, setLoading] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const [error, setError] = useState(false);
  const dispatch = useDispatch();
  const { loqateId, value, handleValueChange, options, handleOptionClick, postcode } = useLoqate(
    props.address,
    props.loqId,
    props.pc,
  );
  const theme = useTheme();

  const fetchResult = async (values: FormValues) => {
    setLoading(true);
    setError(false);

    try {
      const recaptcha = await getCaptchaKey('valuation');
      const result = await axios<ValuationResult>(
        {
          method: 'POST',
          url: `${API_URL}/valuation/instant/recapatcha`,
          data: {
            ...getValuationPayload(values),
            recaptcha,
          },
        },
        dispatch,
      );

      const section = props.mode === 'instant' || result.data.listing ? 'result' : 'request';
      props.onResult(`${section}/${result.data.id}`, result.data);
    } catch (err) {
      setLoading(false);
      setError(true);
    }
  };

  const formik = useFormik({
    initialValues: {
      loqateId: props.loqId || '',
      subBuilding: '',
      buildingName: '',
      buildingNumber: '',
      street: '',
      town: '',
      postcode: '',
      mode: props.mode,
      propertyType: '',
      propertyStyle: '',
      bedrooms: '',
      relationship: '',
      valuationReason: '',
      timeframe: '',
    },
    validationSchema: valuationFormSchema,
    onSubmit: (values) => {
      if (!loading) {
        fetchResult({ ...values, address: value });
      }
    },
  });

  useEffect(() => {
    formik.setFieldValue('propertyStyle', '');
  }, [formik.values.propertyType]);

  useEffect(() => {
    formik.setFieldValue('valuationReason', '');
  }, [formik.values.relationship]);

  useEffect(() => {
    formik.setFieldValue('postcode', postcode);

    // Fixes an issue within formik where using setFieldValue doesn't update validation
    // https://github.com/jaredpalmer/formik/issues/2059
    setTimeout(() => {
      if (postcode) formik.setFieldTouched('postcode', true);
    });

    props.onPostcodeUpdate(postcode);
  }, [postcode]);

  useEffect(() => {
    formik.setFieldValue('loqateId', loqateId);
  }, [loqateId]);

  // generate intial option if coming from search form
  useEffect(() => {
    if (props.address && props.loqId && !options) handleValueChange(props.address);
  }, [props.address, props.loqId]);

  return (
    <form onSubmit={formik.handleSubmit}>
      {!manualEntry && (
        <TextInput
          containerStyle={{ marginTop: 20 }}
          placeholder="Enter your address..."
          label="Address lookup"
          name="addressLookup"
          value={value}
          onValueChange={handleValueChange}
          autoCompleteOptions={options}
          onOptionClick={handleOptionClick}
          error={formik.touched.postcode && formik.errors.postcode}
          autoCompleteText="Enter address manually"
          onAutoCompleteTextClick={() => {
            setManualEntry(true);
            formik.setFieldValue('loqateId', '');
          }}
          testID="addressLookup"
        />
      )}

      {(!!loqateId || manualEntry) && (
        <>
          {manualEntry && (
            <>
              <Row>
                <TextInput
                  placeholder="Flat"
                  label="Flat"
                  containerStyle={{ marginTop: 20, marginRight: 15 }}
                  value={formik.values.subBuilding}
                  onValueChange={formik.handleChange('subBuilding')}
                  name="subBuilding"
                />

                <TextInput
                  placeholder="House Name"
                  label="House Name"
                  containerStyle={{ marginTop: 20 }}
                  value={formik.values.buildingName}
                  onValueChange={formik.handleChange('buildingName')}
                  name="buildingName"
                  error={formik.touched.buildingName && formik.errors.buildingName}
                  onBlur={() => formik.setFieldTouched('buildingName')}
                />
              </Row>

              <TextInput
                placeholder="House Number"
                label="House Number"
                containerStyle={{ marginTop: 20 }}
                value={formik.values.buildingNumber}
                onValueChange={formik.handleChange('buildingNumber')}
                name="buildingNumber"
                error={formik.touched.buildingNumber && formik.errors.buildingNumber}
                onBlur={() => formik.setFieldTouched('buildingNumber')}
              />

              <TextInput
                placeholder="Street"
                label="Street"
                containerStyle={{ marginTop: 20 }}
                value={formik.values.street}
                onValueChange={formik.handleChange('street')}
                name="street"
                error={formik.touched.street && formik.errors.street}
                onBlur={() => formik.setFieldTouched('street')}
              />

              <TextInput
                placeholder="Town"
                label="Town"
                containerStyle={{ marginTop: 20 }}
                value={formik.values.town}
                onValueChange={formik.handleChange('town')}
                name="town"
                error={formik.touched.town && formik.errors.town}
                onBlur={() => formik.setFieldTouched('town')}
              />

              <TextInput
                placeholder="Postcode"
                label="Postcode"
                containerStyle={{ marginTop: 20 }}
                value={formik.values.postcode}
                onValueChange={formik.handleChange('postcode')}
                name="postcode"
                error={formik.touched.postcode && formik.errors.postcode}
                onBlur={() => formik.setFieldTouched('postcode')}
                testID="postcodeInput"
              />
            </>
          )}

          <SelectInput
            containerStyle={{ marginTop: 20 }}
            ariaLabel="Property Type"
            id="propertyType"
            label="Property Type"
            options={PROPERTY_TYPES}
            value={formik.values.propertyType}
            onChange={formik.handleChange('propertyType')}
            placeholder="Please select"
            error={formik.touched.propertyType && formik.errors.propertyType}
            onBlur={() => formik.setFieldTouched('propertyType')}
            testID="propertyTypeSelect"
          />

          {['HOUSE', 'BUNGALOW'].includes(formik.values.propertyType) && (
            <SelectInput
              containerStyle={{ marginTop: 20 }}
              ariaLabel="Property Style"
              id="propertyStyle"
              label="Property Style"
              options={formik.values.propertyType === 'HOUSE' ? HOUSE_STYLES : BUNGALOW_STYLES}
              value={formik.values.propertyStyle}
              onChange={formik.handleChange('propertyStyle')}
              placeholder="Please select"
              error={formik.touched.propertyStyle && formik.errors.propertyStyle}
              onBlur={() => formik.setFieldTouched('propertyStyle')}
              testID="propertyStyleSelect"
            />
          )}

          <SelectInput
            containerStyle={{ marginTop: 20 }}
            ariaLabel="Bedrooms"
            id="bedrooms"
            label="Bedrooms"
            options={BEDROOM_OPTIONS}
            value={formik.values.bedrooms}
            onChange={formik.handleChange('bedrooms')}
            placeholder="Please select"
            error={formik.touched.bedrooms && formik.errors.bedrooms}
            onBlur={() => formik.setFieldTouched('bedrooms')}
            testID="bedroomsSelect"
          />

          <SelectInput
            containerStyle={{ marginTop: 20 }}
            ariaLabel="What is your relationship to this property?"
            id="relationship"
            label="What is your relationship to this property?"
            options={RELATIONSHIP_OPTIONS(props.mode)}
            value={formik.values.relationship}
            onChange={formik.handleChange('relationship')}
            placeholder="Please select"
            error={formik.touched.relationship && formik.errors.relationship}
            onBlur={() => formik.setFieldTouched('relationship')}
            testID="relationshipSelect"
          />

          {props.mode === 'agent' && (
            <SelectInput
              containerStyle={{ marginTop: 20 }}
              ariaLabel="Why would you like a valuation?"
              id="whyValuation"
              label="Why would you like a valuation?"
              options={formik.values.relationship === 'TENANT' ? TENANT_REASON_OPTIONS : REASON_OPTIONS}
              value={formik.values.valuationReason}
              onChange={formik.handleChange('valuationReason')}
              placeholder="Please select"
              error={formik.touched.valuationReason && formik.errors.valuationReason}
              onBlur={() => formik.setFieldTouched('valuationReason')}
              testID="whyValuationSelect"
            />
          )}

          {props.mode === 'agent' &&
            ['SELL', 'LET'].includes(formik.values.valuationReason) &&
            formik.values.relationship !== 'TENANT' && (
              <SelectInput
                containerStyle={{ marginTop: 20 }}
                ariaLabel={`When would you like to ${formik.values.valuationReason.toLowerCase()} the property?`}
                id="when"
                label={`When would you like to ${formik.values.valuationReason.toLowerCase()} the property?`}
                options={WHEN_OPTIONS}
                value={formik.values.timeframe}
                onChange={formik.handleChange('timeframe')}
                placeholder="Please select"
                error={formik.touched.timeframe && formik.errors.timeframe}
                onBlur={() => formik.setFieldTouched('timeframe')}
                testID="whenSelect"
              />
            )}

          <ErrorMessage
            show={error}
            errorHeader="Something's not right"
            errorMessage="There was an issue finding that property. Please check the address or try again later."
          />

          <Button
            containerStyle={{ marginTop: 30, backgroundColor: !formik.isValid ? theme.backgroundLight : undefined }}
            type="submit"
            loading={loading}
            disabled={!formik.isValid}
            color={!formik.isValid ? theme.textLight : ''}
          >
            {props.mode === 'agent' ? 'Next step' : 'Get your valuation'}
          </Button>

          <Recaptcha recaptchaKey={config.recaptchaKey} />
        </>
      )}
    </form>
  );
};

export default InstantValuationForm;
